<template>
  <b-container>
    <b-col>
      <div class="table-wrapper p-2 border">
        <b-row class="mb-3">
          <b-col>
            <h3>Page Segmentation List</h3>
          </b-col>
          <b-col class="text-center">
            <div
              v-if="isLoading"
              class="page-segment-list-spinner-container"
            >
              <b-spinner class="mr-2" />
              <span class="page-segment-list-spinner-text">Loading, please wait...</span>
            </div>
            <div
              v-else-if="isDeleting"
              class="page-segment-list-spinner-container"
            >
              <b-spinner class="mr-2" />
              <span class="spinner-text">Deleting, please wait...</span>
            </div>
            <div
              v-else-if="changingPublishState"
              class="page-segment-list-spinner-container"
            >
              <b-spinner class="mr-2" />
              <span class="spinner-text">Changing publish state, please wait...</span>
            </div>
            <div
              v-else-if="!isLoading && !isDeleting && !changingPublishState && rows.length === 0"
              class="text-center my-4"
            >
              <h3>We do not have any Page Segmentation in the system as yet!</h3>
            </div>
          </b-col>
          <b-col class="text-right">
            <b-button
              variant="primary"
              :disabled="isLoading || isDeleting || changingPublishState || !$can('create', 'Segment')"
              @click="addNewPageSegment()"
            >
              <feather-icon
                icon="PlusCircleIcon"
                size="18"
              />
              <span class="text-nowrap ml-1">Add New Page Segmentation</span>
            </b-button>
          </b-col>
        </b-row>
        <b-card
          ref="pageList"
        >
          <!-- search input -->
          <div class="custom-search d-flex justify-content-end">
            <b-form-group>
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="searchTerm"
                  placeholder="Search Segmentations"
                  type="text"
                  class="d-inline-block"
                  @input="handleSearch"
                />
              </div>
            </b-form-group>
          </div>
          <!-- tables -->
          <vue-good-table
            v-if="!isLoading && rows.length > 0"
            :columns="columns"
            :rows="rows"
            :search-options="{
              enabled: true,
              externalQuery: searchTerm
            }"
            :select-options="{
              enabled: false,
              selectOnCheckboxOnly: true,
              selectionInfoClass: 'custom-class',
              selectionText: 'rows selected',
              clearSelectionText: 'clear',
              disableSelectInfo: true,
              selectAllByGroup: true,
            }"
            :pagination-options="{
              enabled: true,
              perPage:pageLength
            }"
            @on-sort-change="onSortChange"
          >
            <template v-slot:emptystate>
              <div class="text-center my-4">
                No data available
              </div>
            </template>
            <template
              slot="table-row"
              slot-scope="props"
            >
              <!-- Column: Name -->
              <span
                v-if="props.column.field === 'name'"
                class="text-nowrap"
              >
                <span class="text-nowrap">{{ props.row.name }}</span>
              </span>

              <!-- Column: Description -->
              <span
                v-else-if="props.column.field === 'description'"
                class="text-nowrap"
              >
                <span class="text-nowrap">{{ props.row.description }}</span>
              </span>

              <!-- Column: Action -->
              <span v-else-if="props.column.field === 'action'">
                <b-row>
                  <!-- View/Edit Button -->
                  <b-col
                    cols="4"
                    class="px-1"
                  >
                    <b-button
                      size="sm"
                      variant="primary"
                      class="btn-block"
                      :disabled="isLoading || isDeleting || changingPublishState"
                      @click="viewEditSegment(props.row.id)"
                    >
                      Edit
                    </b-button>
                  </b-col>

                  <!-- UnPublish Button -->
                  <b-col
                    v-if="props.row.isLive"
                    cols="4"
                    class="px-1"
                  >
                    <b-button
                      size="sm"
                      variant="warning"
                      class="btn-block"
                      :disabled="isLoading || isDeleting || changingPublishState || !$can('create', 'Segment')"
                      @click="unPublishPageSegment(props.row.id, props.row.name)"
                    >
                      UnPublish
                    </b-button>
                  </b-col>

                  <!-- Delete Button -->
                  <b-col
                    cols="4"
                    class="px-1"
                  >
                    <div :id="`delete-tooltip-${props.row.id}`">
                      <b-button
                        size="sm"
                        variant="danger"
                        class="btn-block"
                        :disabled="isLoading || isDeleting || changingPublishState || !props.row.isLive || !$can('create', 'Segment')"
                        @click="deletePageSegment(props.row.id, props.row.name)"
                      >
                        Delete
                      </b-button>
                    </div>
                    <!-- Tooltip for Delete button -->
                    <b-tooltip
                      :target="`delete-tooltip-${props.row.id}`"
                      triggers="hover"
                    >
                      <template v-if="props.row.state === 'published'">
                        Published segment cannot be deleted. You need to unpublish it first to delete.
                      </template>
                    </b-tooltip>
                  </b-col>
                </b-row>
              </span>

              <!-- Column: Common -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>

            <!-- pagination -->
            <template
              slot="pagination-bottom"
              slot-scope="props"
            >
              <div class="d-flex justify-content-between flex-wrap">

                <!-- page length -->
                <div class="d-flex align-items-center mb-0 mt-1">
                  <span class="text-nowrap ">
                    Showing 1 to
                  </span>
                  <b-form-select
                    v-model="pageLength"
                    :options="pages"
                    class="mx-1"
                    @input="(value)=>props.perPageChanged({currentPerPage:value})"
                  />
                  <span class="text-nowrap"> of {{ props.total }} entries </span>
                </div>

                <!-- pagination -->
                <div>
                  <b-pagination
                    :value="1"
                    :total-rows="props.total"
                    :per-page="pageLength"
                    first-number
                    last-number
                    align="right"
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mt-1 mb-0"
                    @change="handleChangePage"
                    @input="(value)=>props.pageChanged({currentPage:value})"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </div>
              </div>
            </template>
          </vue-good-table>
        </b-card>
      </div>
    </b-col>
  </b-container>
</template>

<script>
import {
    BCard, BPagination, BFormGroup, BFormInput, BFormSelect, BButton, BSpinner, BRow, BCol, BContainer, BTooltip,
} from 'bootstrap-vue'

import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
    getPageSegments,
    deletePageSegment,
    unPublishSegmentPage, getUserSegments,
} from '@/coreApi/coreApiSegment'
import { getCurrentProjectData } from '@/auth/utils'

export default {
    name: 'PageSegmentList',
    components: {
        BContainer,
        BCol,
        BRow,
        BCard,
        VueGoodTable,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BButton,
        BSpinner,
        BTooltip,
    },
    data() {
        return {
            isLoading: true,
            isDeleting: false,
            changingPublishState: false,
            changingPublishStateRowId: null,
            project: [],
            metricComponents: [],
            dimensionComponents: [],
            orderBy: [],
            filterData: {},
            pageLength: 10,
            dir: false,
            pages: ['10', '50', '100'],
            columns: [
                {
                    label: 'Name',
                    field: 'name',
                },
                {
                    label: 'Description',
                    field: 'description',
                    sortable: false,
                },
                {
                    label: 'Count',
                    field: 'cnt',
                    sortable: false,
                },
                {
                    label: 'Action',
                    field: 'action',
                    sortable: false,
                },
            ],
            rows: [],
            searchTerm: '',
        }
    },

    created() {
        this.setPageList()
        // this.setFormData()
    },
    methods: {
        addNewPageSegment() {
            this.$router.push({
                name: 'project-frontend-dashboard-segment-add',
                params: { segment_type: 'page' },
            })
        },

        viewEditSegment(segmentId) {
            this.$router.push({
                name: 'project-frontend-dashboard-segment-edit',
                params: { segment_type: 'page', segment_id: segmentId },
            })
        },

        unPublishPageSegment(id, name) {
            this.$bvModal
                .msgBoxConfirm(`Please confirm that you want to unpublish page segment: ${name}.`, {
                    title: 'Please Confirm',
                    size: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Yes',
                    cancelTitle: 'No',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    if (value) {
                        window.scrollTo(0, 0)
                        this.changingPublishState = true

                        unPublishSegmentPage(id)
                            .then(response => {
                                if (response.data && response.data.data.success) {
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: {
                                            title: 'Segment unPublished successfully',
                                            icon: 'CheckIcon',
                                            variant: 'success',
                                        },
                                    })
                                    const rowIndex = this.rows.findIndex(row => row.id === id)
                                    if (rowIndex !== -1) {
                                        this.$set(this.rows[rowIndex], 'state', 'tmp')
                                    }
                                    this.changingPublishState = false
                                }
                            })
                            .catch(error => {
                                if (error.response.data.error.message.general) {
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: {
                                            title: 'Error',
                                            icon: 'ErrorIcon',
                                            variant: 'danger',
                                            text: error.response.data.error.message.general[0],
                                        },
                                    })
                                }
                            })
                    } else {
                        this.changingPublishState = false
                    }
                })
        },

        setPageList() {
            this.isLoading = true
            return getPageSegments()
                .then(res => {
                    if (res.data && res.data.pages) {
                        // console.log('res.data.pages', res.data.pages)
                        // console.log('res.data.pages_live', res.data.pages_live)

                        const pagesArray = Object.values(res.data.pages)

                        this.rows = pagesArray.map(page => {
                            let cntValue = 0

                            let isLive = false
                            if (res.data.pages_live) {
                                const matchedLivePage = Object.values(res.data.pages_live).find(pageLive => pageLive.alias === page.alias)
                                if (matchedLivePage) {
                                    cntValue = matchedLivePage.cnt
                                    isLive = true
                                }
                            }

                            return { ...page, cnt: cntValue, isLive }
                        })
                    } else {
                        this.rows = []
                    }
                    this.isLoading = false
                })
                .catch(error => {
                    console.error('Error fetching data:', error)
                    this.isLoading = false
                    this.rows = []
                })
        },
        // setPageList() {
        //     this.isLoading = true
        //     return getPageSegments()
        //         .then(res => {
        //             if (res.data && res.data.pages) {
        //                 console.log('res.data.pages', res.data.pages)
        //                 console.log('res.data.pages_live', res.data.pages_live)
        //                 this.rows = Object.values(res.data.pages)
        //             } else {
        //                 this.rows = []
        //             }
        //             this.isLoading = false
        //         })
        //         .catch(error => {
        //             console.error('Error fetching data:', error)
        //             this.isLoading = false
        //             this.rows = []
        //         })
        // },

        setFormData() {
        // console.log(" getCurrentProjectData()",  getCurrentProjectData())
            const projectData = getCurrentProjectData()

            // console.log('projectData', projectData)
            //   console.log('projectData.projectDetails', projectData.projectDetails)
            //   console.log('projectData.projectDetails.filterData', projectData.projectDetails.filterData)
            // console.log('projectData.projectDetails.segmentComponentData.segmentProjectActionFilterComponents', projectData.projectDetails.segmentComponentData.segmentProjectActionFilterComponents)

            if (projectData && projectData.projectDetails.filterData) {
                this.filterData = projectData.projectDetails.filterData
                this.metricComponents = projectData.projectDetails.segmentComponentData.segmentProjectPageMetricComponents
                this.dimensionComponents = projectData.projectDetails.segmentComponentData.segmentProjectPageDimensionComponents
                this.segmentType = 'page'
                this.editMode = false
            } else {
                this.filterData = {}
                this.filterComponents = []
            }
        },

        deletePageSegment(id, name) {
            console.log('delete', id, name)
            this.$bvModal
                .msgBoxConfirm(`Please confirm that you want to delete page segment: ${name}.`, {
                    title: 'Please Confirm',
                    size: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Yes',
                    cancelTitle: 'No',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    if (value) {
                        this.isDeleting = true
                        deletePageSegment(id)
                            .then(response => {
                                this.isDeleting = false
                                if (response.data.success) {
                                    this.rows = this.rows.filter(row => row.id !== id)
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: {
                                            title: 'Page Segment deleted successfully',
                                            icon: 'CheckIcon',
                                            variant: 'success',
                                        },
                                    })
                                } else if (response.data.error && response.data.error.message.general) {
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: {
                                            title: 'Error',
                                            icon: 'ErrorIcon',
                                            variant: 'danger',
                                            text: response.data.error.message.general[0],
                                        },
                                    })
                                }
                            })
                            .catch(error => {
                                this.isDeleting = false
                                console.error('Unexpected error:', error)
                                // Show an error toast
                                this.$toast({
                                    component: ToastificationContent,
                                    position: 'top-right',
                                    props: {
                                        title: 'Unexpected Error',
                                        icon: 'ErrorIcon',
                                        variant: 'danger',
                                        text: 'An unexpected error occurred.',
                                    },
                                })
                            })
                    } else {
                        this.isDeleting = false
                    }
                })
        },

        closeForm() {
            this.setPageList()
        },

        handleSearch(searching) {
        },
        handleChangePage(page) {
        },
        handlePageChange(active) {
        },
        onSortChange(params) {
        },
    },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table';
</style>
